import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './dialog';
import { Search, MapPin, Navigation, Loader2 } from 'lucide-react';

// Custom marker icon
const startPointIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map clicks
const LocationMarker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? (
    <Marker 
      position={position} 
      icon={startPointIcon}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const marker = e.target;
          const pos = marker.getLatLng();
          setPosition([pos.lat, pos.lng]);
        }
      }}
    />
  ) : null;
};

// Component to recenter map
const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  return null;
};

/**
 * MapPicker - Interactive map for selecting a location
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.initialPosition - {lat, lng} initial position
 * @param {function} props.onSelect - Callback when location is selected: (lat, lng, address) => void
 * @param {string} props.title - Dialog title
 */
const MapPicker = ({ 
  open, 
  onOpenChange, 
  initialPosition = { lat: 2.8120978, lng: -60.7098542 },
  onSelect,
  title = "Selecionar no Mapa"
}) => {
  const [position, setPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [address, setAddress] = useState('');
  const [mapCenter, setMapCenter] = useState([initialPosition.lat, initialPosition.lng]);

  // Initialize position when dialog opens
  useEffect(() => {
    if (open) {
      if (initialPosition.lat && initialPosition.lng) {
        setPosition([initialPosition.lat, initialPosition.lng]);
        setMapCenter([initialPosition.lat, initialPosition.lng]);
      } else {
        setPosition(null);
        setMapCenter([2.8120978, -60.7098542]); // Default to Boa Vista
      }
    }
  }, [open, initialPosition]);

  // Reverse geocode when position changes
  useEffect(() => {
    const reverseGeocode = async () => {
      if (!position) return;
      
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position[0]}&lon=${position[1]}&zoom=18&addressdetails=1`,
          { headers: { 'Accept-Language': 'pt-BR' } }
        );
        const data = await response.json();
        if (data.display_name) {
          setAddress(data.display_name);
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      }
    };

    reverseGeocode();
  }, [position]);

  // Search for address
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
        { headers: { 'Accept-Language': 'pt-BR' } }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        const newPos = [parseFloat(lat), parseFloat(lon)];
        setPosition(newPos);
        setMapCenter(newPos);
        setAddress(display_name);
      } else {
        alert('Endereço não encontrado. Tente ser mais específico.');
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('Erro ao buscar endereço');
    } finally {
      setSearching(false);
    }
  };

  // Confirm selection
  const handleConfirm = () => {
    if (position && onSelect) {
      onSelect(position[0], position[1], address);
    }
    onOpenChange(false);
  };

  // Use current location
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newPos = [pos.coords.latitude, pos.coords.longitude];
          setPosition(newPos);
          setMapCenter(newPos);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Não foi possível obter sua localização');
        }
      );
    } else {
      alert('Geolocalização não suportada pelo navegador');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Clique no mapa ou arraste o marcador para selecionar o ponto inicial da rota
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 overflow-y-auto flex-1 min-h-0">
          {/* Search bar */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar endereço..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="map-search-input"
              />
            </div>
            <Button onClick={handleSearch} disabled={searching} variant="outline">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
            <Button onClick={handleUseCurrentLocation} variant="outline" title="Usar minha localização">
              <Navigation className="h-4 w-4" />
            </Button>
          </div>

          {/* Map */}
          <div className="h-[300px] w-full rounded-lg overflow-hidden border">
            <MapContainer
              center={mapCenter}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapController center={mapCenter} />
              <LocationMarker position={position} setPosition={setPosition} />
            </MapContainer>
          </div>

          {/* Selected address */}
          {address && (
            <div className="bg-muted p-3 rounded-lg">
              <Label className="text-xs text-muted-foreground">Endereço selecionado:</Label>
              <p className="text-sm font-medium mt-1">{address}</p>
              {position && (
                <p className="text-xs text-muted-foreground mt-1">
                  Coordenadas: {position[0].toFixed(6)}, {position[1].toFixed(6)}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!position} data-testid="confirm-map-selection">
            Confirmar Localização
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { MapPicker };
